import { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { IntlKey } from 'locales';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { HandledWorkspace } from 'state/forms/models';
import { SponsoFee, SponsoFeeField } from 'state/quotes/sponsoFees/sponsoFees';
import {
  NextTradeAuthorizationResult,
  PriceEnhancement,
  PriceEnhancementField,
} from 'state/quotes/trades/trades.model';

export type CommonTradeAction = ReturnType<(typeof commonTradeAction)[keyof typeof commonTradeAction]>;
export type TradeAuthReceived = ReturnType<typeof commonTradeAction.tradeAuthReceived>;
export const commonTradeAction = {
  tradeAuthReceived(
    workspace: HandledWorkspace,
    tabId: string,
    quoteId: string,
    { status, rejections, expirationDelayInSeconds }: NextTradeAuthorizationResult,
    when: Date,
  ) {
    return {
      type: 'TRADE_AUTH_RECEIVED' as const,
      workspace,
      tabId,
      quoteId,
      status,
      rejections,
      expirationDelayInSeconds,
      when,
    };
  },
  tradeAuthTimeout(workspace: HandledWorkspace, tabId: string, quoteId: string) {
    return {
      type: 'TRADE_AUTH_TIMEOUT' as const,
      workspace,
      tabId,
      quoteId,
    };
  },
  quotePriceEnhanced(
    workspace: Exclude<HandledWorkspace, 'essential'>,
    quoteId: string,
    key: PriceEnhancementField,
    value: PriceEnhancement[PriceEnhancementField],
  ) {
    return {
      type: 'QUOTE_PRICE_ENHANCED' as const,
      workspace,
      quoteId,
      key,
      value,
    };
  },

  bookingRequested(
    workspace: HandledWorkspace,
    tabId: string,
    formId: string,
    withExecution: boolean,
    frontReconciliationId: string,
    quoteId: string,
  ) {
    return {
      type: 'BOOKING_REQUESTED' as const,
      workspace,
      tabId,
      formId,
      withExecution,
      frontReconciliationId,
      quoteId,
    };
  },
  quoteSponsoFee(
    workspace: Exclude<HandledWorkspace, 'essential'>,
    quoteId: string,
    key: SponsoFeeField,
    value: SponsoFee[SponsoFeeField],
  ) {
    return {
      type: 'QUOTE_SPONSO_FEE' as const,
      workspace,
      quoteId,
      key,
      value,
    };
  },
  quoteTradeRequested(workspace: HandledWorkspace, tabId: string, quoteId: string) {
    return {
      type: 'QUOTE_TRADE_REQUESTED' as const,
      workspace,
      tabId,
      quoteId,
    };
  },
  quoteTradeSent(workspace: HandledWorkspace, tabId: string, quoteId: string) {
    return {
      type: 'QUOTE_TRADE_SENT' as const,
      workspace,
      tabId,
      quoteId,
    };
  },
  quoteTradeFailed(workspace: HandledWorkspace, tabId: string, quoteId: string, error: ApiErrorMessage, cause: any) {
    return {
      type: 'QUOTE_TRADE_FAILED' as const,
      workspace,
      quoteId,
      tabId,
      error,
      cause,
    };
  },
  quoteTradeRejected(
    workspace: HandledWorkspace,
    tabId: string,
    quoteId: string,
    rejections: readonly NextWorkflowError[],
    when: Date,
  ) {
    return {
      type: 'QUOTE_TRADE_REJECTED' as const,
      workspace,
      tabId,
      quoteId,
      rejections,
      when,
    };
  },
  quoteTradeRequestRejected(workspace: HandledWorkspace, tabId: string, quoteId: string, error: IntlKey) {
    return {
      type: 'QUOTE_TRADE_REQUEST_REJECTED' as const,
      workspace,
      tabId,
      quoteId,
      error,
    };
  },
  quoteTradeDone(
    workspace: HandledWorkspace,
    tabId: string,
    quoteId: string,
    status: string,
    when: Date,
    bookingSkipped?: boolean,
  ) {
    return {
      type: 'QUOTE_TRADE_DONE' as const,
      workspace,
      tabId,
      quoteId,
      status,
      bookingSkipped,
      when,
    };
  },
};
